import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, AdminLayout } from "./AdminLayout";
import { MessageSquare, Briefcase, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSubmissions = () => {
  const { token } = useAuth();
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [processSubmissions, setProcessSubmissions] = useState([]);
  const [activeTab, setActiveTab] = useState("contact");
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const [contact, process] = await Promise.all([
        axios.get(`${API}/contact/submissions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/process/submissions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setContactSubmissions(contact.data);
      setProcessSubmissions(process.data);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (err) {
      console.debug("Date parse failed:", err);
      return dateString;
    }
  };

  // Clear selection when switching tabs
  const switchTab = (tab) => {
    setActiveTab(tab);
    setSelected(new Set());
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= 5) {
          toast.error("You can select up to 5 items at a time");
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    const count = selected.size;
    if (!window.confirm(`Delete ${count} submission${count > 1 ? "s" : ""}? This cannot be undone.`)) return;

    setDeleting(true);
    const ids = Array.from(selected);
    const endpoint = activeTab === "contact" ? "contact" : "process";
    try {
      await axios.post(`${API}/${endpoint}/submissions/delete`, { ids }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${count} submission${count > 1 ? "s" : ""} deleted`);
      setSelected(new Set());
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to delete submissions");
    }
    setDeleting(false);
  };

  const currentList = activeTab === "contact" ? contactSubmissions : processSubmissions;
  const selectAllVisible = () => {
    const ids = currentList.slice(0, 5).map((s) => s.id);
    setSelected(new Set(ids));
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-submissions">
        <h1 className="heading-3">Form Submissions</h1>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-its-teal-light/20 pb-2">
          <button
            onClick={() => switchTab("contact")}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === "contact"
                ? "bg-its-gold/20 text-its-gold border-b-2 border-its-gold"
                : "text-its-offwhite hover:text-white"
            }`}
          >
            <MessageSquare size={18} />
            Contact Messages ({contactSubmissions.length})
          </button>
          <button
            onClick={() => switchTab("process")}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === "process"
                ? "bg-its-gold/20 text-its-gold border-b-2 border-its-gold"
                : "text-its-offwhite hover:text-white"
            }`}
          >
            <Briefcase size={18} />
            Project Inquiries ({processSubmissions.length})
          </button>
        </div>

        {/* Delete bar */}
        {currentList.length > 0 && (
          <div className="flex items-center justify-between" data-testid="delete-bar">
            <div className="flex items-center gap-3">
              <button
                onClick={selectAllVisible}
                className="text-xs text-its-offwhite/60 hover:text-white transition-colors underline"
                data-testid="select-all-btn"
              >
                Select first 5
              </button>
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-its-offwhite/60 hover:text-white transition-colors underline"
                  data-testid="clear-selection-btn"
                >
                  Clear selection
                </button>
              )}
              {selected.size > 0 && (
                <span className="text-xs text-its-gold">{selected.size} selected</span>
              )}
            </div>
            {selected.size > 0 && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300 transition-colors text-sm font-medium"
                data-testid="delete-selected-btn"
              >
                <Trash2 size={16} />
                {deleting ? "Deleting..." : `Delete ${selected.size}`}
              </button>
            )}
          </div>
        )}

        {/* Contact Submissions */}
        {activeTab === "contact" && (
          <div className="space-y-4">
            {contactSubmissions.length === 0 ? (
              <div className="glass-card text-center py-8">
                <p className="text-its-offwhite/60">No contact submissions yet</p>
              </div>
            ) : (
              contactSubmissions.map((sub) => (
                <div
                  key={sub.id}
                  className={`glass-card cursor-pointer transition-all ${
                    selected.has(sub.id)
                      ? "ring-2 ring-its-gold/60 bg-its-gold/5"
                      : "hover:border-its-teal-light/30"
                  }`}
                  onClick={() => toggleSelect(sub.id)}
                  data-testid={`contact-sub-${sub.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selected.has(sub.id)}
                        onChange={() => toggleSelect(sub.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 w-4 h-4 rounded border-its-teal-light/30 accent-its-gold"
                        data-testid={`checkbox-${sub.id}`}
                      />
                      <div>
                        <h3 className="font-medium text-white">{sub.name}</h3>
                        <p className="text-sm text-its-teal-light">{sub.phone}</p>
                      </div>
                    </div>
                    <span className="text-xs text-its-offwhite/60">{formatDate(sub.created_at)}</span>
                  </div>
                  <p className="text-its-offwhite/90 whitespace-pre-wrap pl-7">{sub.message}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Process Submissions */}
        {activeTab === "process" && (
          <div className="space-y-4">
            {processSubmissions.length === 0 ? (
              <div className="glass-card text-center py-8">
                <p className="text-its-offwhite/60">No project inquiries yet</p>
              </div>
            ) : (
              processSubmissions.map((sub) => {
                let extraFields = {};
                try { extraFields = JSON.parse(sub.description || "{}"); } catch (err) { console.debug("Description not JSON:", err); }
                const hasExtraFields = Object.values(extraFields).some(v => v && String(v).trim());
                const displayName = sub.name || sub.company || sub.email || "Anonymous";

                return (
                <div
                  key={sub.id}
                  className={`glass-card cursor-pointer transition-all ${
                    selected.has(sub.id)
                      ? "ring-2 ring-its-gold/60 bg-its-gold/5"
                      : "hover:border-its-teal-light/30"
                  }`}
                  onClick={() => toggleSelect(sub.id)}
                  data-testid={`process-sub-${sub.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selected.has(sub.id)}
                        onChange={() => toggleSelect(sub.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 w-4 h-4 rounded border-its-teal-light/30 accent-its-gold"
                        data-testid={`checkbox-${sub.id}`}
                      />
                      <div>
                        <h3 className="font-medium text-white">{displayName}</h3>
                        {sub.email && <p className="text-sm text-its-teal-light">{sub.email}</p>}
                      </div>
                    </div>
                    <span className="text-xs text-its-offwhite/60">{formatDate(sub.created_at)}</span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pl-7">
                    {sub.company && (
                      <div>
                        <span className="text-xs text-its-offwhite/60 block mb-1">Company</span>
                        <span className="text-sm text-white">{sub.company}</span>
                      </div>
                    )}
                    {sub.phone && (
                      <div>
                        <span className="text-xs text-its-offwhite/60 block mb-1">Phone</span>
                        <span className="text-sm text-white">{sub.phone}</span>
                      </div>
                    )}
                    {sub.project_type && (
                      <div>
                        <span className="text-xs text-its-offwhite/60 block mb-1">Industry</span>
                        <span className="text-sm text-white">{sub.project_type}</span>
                      </div>
                    )}
                    {sub.timeline && (
                      <div>
                        <span className="text-xs text-its-offwhite/60 block mb-1">Update Frequency</span>
                        <span className="text-sm text-white">{sub.timeline}</span>
                      </div>
                    )}
                  </div>

                  {hasExtraFields && (
                    <div className="space-y-3 border-t border-its-teal-light/10 pt-4 pl-7">
                      {extraFields.products_services && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Products / Services</span>
                          <p className="text-sm text-its-offwhite/90 whitespace-pre-wrap">{extraFields.products_services}</p>
                        </div>
                      )}
                      {extraFields.business_goals && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Business Goals</span>
                          <p className="text-sm text-its-offwhite/90 whitespace-pre-wrap">{extraFields.business_goals}</p>
                        </div>
                      )}
                      {extraFields.has_website && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Has Website</span>
                          <p className="text-sm text-its-offwhite/90">{extraFields.has_website}</p>
                        </div>
                      )}
                      {extraFields.current_website && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Current Website</span>
                          <p className="text-sm text-its-offwhite/90">{extraFields.current_website}</p>
                        </div>
                      )}
                      {extraFields.website_likes && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Website Likes</span>
                          <p className="text-sm text-its-offwhite/90 whitespace-pre-wrap">{extraFields.website_likes}</p>
                        </div>
                      )}
                      {extraFields.website_dislikes && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Website Dislikes</span>
                          <p className="text-sm text-its-offwhite/90 whitespace-pre-wrap">{extraFields.website_dislikes}</p>
                        </div>
                      )}
                      {extraFields.website_purpose && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Website Purpose</span>
                          <p className="text-sm text-its-offwhite/90 whitespace-pre-wrap">{extraFields.website_purpose}</p>
                        </div>
                      )}
                      {extraFields.example_websites && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Example Websites</span>
                          <p className="text-sm text-its-offwhite/90 whitespace-pre-wrap">{extraFields.example_websites}</p>
                        </div>
                      )}
                      {extraFields.example_websites_likes && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">What They Like About Examples</span>
                          <p className="text-sm text-its-offwhite/90 whitespace-pre-wrap">{extraFields.example_websites_likes}</p>
                        </div>
                      )}
                      {extraFields.additional_info && (
                        <div>
                          <span className="text-xs text-its-offwhite/60 block mb-1">Additional Info</span>
                          <p className="text-sm text-its-offwhite/90 whitespace-pre-wrap">{extraFields.additional_info}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {!hasExtraFields && sub.description && (() => { try { JSON.parse(sub.description); return false; } catch (_e) { return true; } })() && (
                    <div className="pl-7">
                      <span className="text-xs text-its-offwhite/60 block mb-1">Description</span>
                      <p className="text-its-offwhite/90 whitespace-pre-wrap">{sub.description}</p>
                    </div>
                  )}
                </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSubmissions;
